// src/components/SpiderChart.tsx
// Extracted Radar Chart component for multi-persona readiness visualization

import React, { memo } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { SpiderChartData } from '../types';
import HelpTooltip from '../../../shared/components/HelpTooltip';

interface SpiderChartProps {
  data: SpiderChartData[];
  isSimulating?: boolean;
  title?: string;
  height?: number;
}

const SpiderChartComponent: React.FC<SpiderChartProps> = ({ 
  data, 
  isSimulating = false, 
  title = "📊 Readiness Spider Chart",
  height = 300 
}) => {
  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: 'var(--ra-glass-bg)', 
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{ color: 'var(--ra-text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {title} {isSimulating && '(Simulation)'} <HelpTooltip contentKey="defense.spiderChart" />
      </h3>
      
      {data.length === 0 ? (
        <div style={{ 
          height: height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'var(--ra-text-secondary)',
          fontSize: '14px'
        }}>
          No readiness data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <RadarChart data={data} key={`radar-${isSimulating ? 'sim' : 'orig'}`}>
            <PolarGrid />
            <PolarAngleAxis dataKey="category" tick={{ fontSize: 12 }} />
            <PolarRadiusAxis
              angle={30}
              domain={[0, 100]}
              tick={false}
              tickCount={6}
            />
            <Radar 
              name="Original" 
              dataKey="original" 
              stroke="#6c757d" 
              fill="#6c757d" 
              fillOpacity={0.1}
              strokeWidth={2}
              isAnimationActive={false}
            />
            <Radar 
              name="Current" 
              dataKey="current" 
              stroke={isSimulating ? "#e74c3c" : "#6f42c1"} 
              fill={isSimulating ? "#e74c3c" : "#6f42c1"} 
              fillOpacity={0.3}
              strokeWidth={3}
              isAnimationActive={false}
            />
          </RadarChart>
        </ResponsiveContainer>
      )}
      
      <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--ra-text-secondary)', marginTop: '10px' }}>
        <span style={{ color: '#6c757d' }}>● Original Assessment</span>
        <span style={{ 
          marginLeft: '20px', 
          color: isSimulating ? "#e74c3c" : "#6f42c1" 
        }}>
          ● Current {isSimulating ? '(Simulation)' : ''}
        </span>
      </div>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
const SpiderChart = memo(SpiderChartComponent, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders unless data actually changes
  return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
         prevProps.isSimulating === nextProps.isSimulating;
});

export default SpiderChart;